import onboardingApi, {
  isOnboardingRequestCanceled,
  ONBOARDING_COMPLETION_TIMEOUT_MS,
} from '@/services/axios.onboarding.service';
import { resolveBusinessType } from '@/services/onboarding-company';
import {
  createRegistrationResumePayload,
  createRegistrationStartPayload,
} from '@/services/onboarding.payload';

export { isOnboardingRequestCanceled };

const DEFAULT_EMAIL_CODE_RETRY_SECONDS = 60;
const DEFAULT_EMAIL_CODE_TTL_SECONDS = 600;

function parseRetryAfterSeconds(header) {
  const seconds = Number.parseInt(header, 10);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
}

function createApiError(error, fallbackMessage) {
  const apiError = error.response?.data?.error;
  const normalizedError = new Error(apiError?.message || fallbackMessage);
  const status = error.response?.status;

  normalizedError.code = apiError?.code || error.code;
  normalizedError.status = status;
  normalizedError.retryAfterSeconds = parseRetryAfterSeconds(error.response?.headers?.['retry-after']);
  // Сеть/таймаут/5xx/429 — повторить тот же запрос безопасно, состояние на клиенте не теряем.
  normalizedError.transient = !status || status >= 500 || status === 429;

  return normalizedError;
}

function positiveInteger(value, fallback) {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : fallback;
}

function mapManagement(management) {
  return {
    name: management?.name || '',
    post: management?.post || '',
  };
}

function mapCompanySearchItem(company) {
  return {
    id: company.ogrn || company.inn,
    inn: company.inn,
    kpp: company.kpp || '',
    ogrn: company.ogrn,
    name: company.name,
    shortName: company.short_name || company.name,
    region: company.region || '',
    registeredOn: company.registered_on || null,
    terminatedOn: company.terminated_on || null,
    registryKind: company.registry_kind,
    businessType: resolveBusinessType({ registryKind: company.registry_kind }),
    management: mapManagement(company.management),
  };
}

function mapCompanyDetails(company) {
  return {
    id: company.ogrn || company.inn,
    inn: company.inn,
    kpp: company.kpp || '',
    ogrn: company.ogrn,
    name: company.name,
    shortName: company.name,
    address: company.address || '',
    registryKind: company.registry_kind,
    businessType: resolveBusinessType({ registryKind: company.registry_kind }),
    sourceUpdatedOn: company.source_updated_on || null,
    management: mapManagement(company.management),
  };
}

function mapBank(bank) {
  return {
    id: bank.bik,
    bik: bank.bik,
    name: bank.name,
    correspondentAccount: bank.corr_account || '',
    city: bank.city || '',
    sourceUpdatedOn: bank.source_updated_on || null,
    checkedAt: bank.checked_at || null,
  };
}

function minorToRubles(value) {
  return Number(value) / 100;
}

function mapTariff(tariff) {
  return {
    id: tariff.code,
    tariffId: tariff.id,
    code: tariff.code,
    version: tariff.version,
    weight: tariff.weight,
    name: tariff.name,
    price: minorToRubles(tariff.prices.monthlyAmountMinor),
    waiterAvailable: tariff.code !== 'tips',
    waiterSeats: tariff.includedSeats.waiter,
    learningSeats: tariff.includedSeats.training,
    additionalWaiterPrice: minorToRubles(tariff.prices.additionalWaiterSeatMinor),
    additionalLearningPrice: minorToRubles(tariff.prices.additionalTrainingSeatMinor),
    tipsMode: tariff.tips.mode,
    tipsTrainingBonusSeats: tariff.tips.trainingBonusSeats,
    availableBillingPeriods: [...tariff.availableBillingPeriods],
    currency: tariff.currency,
    description: '',
  };
}

export async function searchCompanies(query, { signal } = {}) {
  try {
    const response = await onboardingApi.get('/companies/search', {
      params: {
        query: String(query || '').trim(),
      },
      signal,
    });

    return response.data.data.items.map(mapCompanySearchItem);
  } catch (error) {
    if (isOnboardingRequestCanceled(error)) throw error;
    throw createApiError(error, 'Не удалось найти компанию');
  }
}

export async function getCompanyDetails(inn, { signal } = {}) {
  try {
    const response = await onboardingApi.get(`/companies/${encodeURIComponent(inn)}/details`, {
      signal,
    });

    return mapCompanyDetails(response.data.data);
  } catch (error) {
    if (isOnboardingRequestCanceled(error)) throw error;
    throw createApiError(error, 'Не удалось загрузить данные компании');
  }
}

export async function getBankByBik(bik, { signal } = {}) {
  try {
    const response = await onboardingApi.get(`/banks/${encodeURIComponent(bik)}`, {
      signal,
    });

    return mapBank(response.data.data);
  } catch (error) {
    if (isOnboardingRequestCanceled(error)) throw error;
    throw createApiError(error, 'Не удалось найти банк');
  }
}

export async function getAvailableTariffs() {
  try {
    const response = await onboardingApi.get('/tariffs');
    return response.data.data
      .map(mapTariff)
      .sort((left, right) => left.weight - right.weight || left.code.localeCompare(right.code));
  } catch (error) {
    throw createApiError(error, 'Не удалось загрузить тарифы');
  }
}

function createContractError(message) {
  const error = new Error(message);
  error.code = 'ONBOARDING_BAD_RESPONSE';
  error.transient = true;
  return error;
}

export async function requestEmailCode({ region, email }) {
  let response;
  try {
    response = await onboardingApi.post('/registrations/email-code/request', {
      region,
      email: String(email || '').trim(),
    });
  } catch (error) {
    throw createApiError(error, 'Не удалось отправить код подтверждения');
  }

  const data = response.data?.data || {};
  if (!data.requestId) throw createContractError('Сервис не вернул идентификатор запроса кода');

  return {
    requestId: data.requestId,
    retryAfterSeconds: positiveInteger(data.retryAfterSeconds, DEFAULT_EMAIL_CODE_RETRY_SECONDS),
    expiresInSeconds: positiveInteger(data.expiresInSeconds, DEFAULT_EMAIL_CODE_TTL_SECONDS),
  };
}

export async function confirmEmailCode({ requestId, code }) {
  let response;
  try {
    response = await onboardingApi.post('/registrations/email-code/confirm', {
      requestId,
      code,
    });
  } catch (error) {
    throw createApiError(error, 'Не удалось подтвердить код');
  }

  const token = response.data?.data?.emailVerificationToken;
  if (!token) throw createContractError('Сервис не вернул подтверждение email');

  return token;
}

export async function startOnboardingRegistration(payload) {
  try {
    const response = await onboardingApi.post(
      '/registrations/start',
      createRegistrationStartPayload(payload),
    );
    return response.data.data;
  } catch (error) {
    throw createApiError(error, 'Не удалось создать аккаунт');
  }
}

export async function resumeOnboardingRegistration(payload) {
  try {
    const response = await onboardingApi.post(
      '/registrations/resume',
      createRegistrationResumePayload(payload),
    );
    return response.data.data;
  } catch (error) {
    throw createApiError(error, 'Не удалось продолжить регистрацию');
  }
}

export async function getCurrentOnboardingApplication(onboardingToken) {
  try {
    const response = await onboardingApi.get('/applications/current', {
      headers: {
        Authorization: `Onboarding ${onboardingToken}`,
      },
    });
    return response.data.data;
  } catch (error) {
    throw createApiError(error, 'Не удалось восстановить onboarding-сессию');
  }
}

export async function saveCurrentOnboardingState(payload, onboardingToken) {
  try {
    const response = await onboardingApi.patch('/applications/current', payload, {
      headers: {
        Authorization: `Onboarding ${onboardingToken}`,
      },
    });
    return response.data.data;
  } catch (error) {
    throw createApiError(error, 'Не удалось сохранить прогресс onboarding');
  }
}

export async function completeOnboardingApplication(
  payload,
  { onboardingToken, idempotencyKey },
) {
  try {
    const response = await onboardingApi.post('/applications/current/complete', payload, {
      timeout: ONBOARDING_COMPLETION_TIMEOUT_MS,
      headers: {
        Authorization: `Onboarding ${onboardingToken}`,
        'Idempotency-Key': idempotencyKey,
      },
    });
    return response.data.data;
  } catch (error) {
    throw createApiError(error, 'Не удалось создать организацию');
  }
}
